import { useEffect, useContext } from 'react'
import _ from 'lodash'

import Typography from '@mui/material/Typography'
import { AppContext } from '/providers/app-context'
import { PassContext } from '/components/editor/pass-context'

import ApplePopupEditor from '/components/editor/apple/popup-editor'
import GooglePopupEditor from '/components/editor/google/popup-editor'

import appleDataSelector from '/components/editor/apple/data-selector'
import googleDataSelector from '/components/editor/google/data-selector'

const googleSources = {
  generic: "object",
  giftCard: "class",
  eventTicket: "class",
  loyalty: "class",
  offer: "class"
}

const appleRequiredImages = {
  generic: {
    icon: { path: "meta.images.icon", required: true },
    logo: { path: "meta.images.logo", required: true },
    thumbnail: { path: "meta.images.thumbnail", required: false }
  },
  coupon: {
    icon: { path: "meta.images.icon", required: true },
    logo: { path: "meta.images.logo", required: true },
    strip: { path: "meta.images.strip", required: false }
  },
  storeCard: {
    icon: { path: "meta.images.icon", required: true },
    logo: { path: "meta.images.logo", required: true },
    strip: { path: "meta.images.strip", required: false }
  },
  eventTicket: {
    BACKGROUND: {
      icon: { path: "meta.images.icon", required: true },
      logo: { path: "meta.images.logo", required: true },
      thumbnail: { path: "meta.images.thumbnail", required: false },
      background: { path: "meta.images.background", required: true }
    },
    null: {
      icon: { path: "meta.images.icon", required: true },
      logo: { path: "meta.images.logo", required: true },
      strip: { path: "meta.images.strip", required: false }
    }
  },
  boardingPass: {
    icon: { path: "meta.images.icon", required: true },
    logo: { path: "meta.images.logo", required: true },
  }
}

const googleRequiredImages = {
  generic: { // generic
    logo: { path: "logo.sourceUri.uri", required: true },
    wide_logo: { path: "wideLogo.sourceUri.uri", required: false },
    hero: { path: "heroImage.sourceUri.uri", required: false },
  },
  offer: { // offer
    logo: { path: "titleImage.sourceUri.uri", required: true },
    wide_logo: { path: "wideTitleImage.sourceUri.uri", required: false },
    hero: { path: "heroImage.sourceUri.uri", required: false },
  },
  loyalty: { // loyalty
    logo: { path: "programLogo.sourceUri.uri", required: true },
    wide_logo: { path: "wideProgramLogo.sourceUri.uri", required: false },
    hero: { path: "heroImage.sourceUri.uri", required: false },
  },
  eventTicket: { // eventTicket
    logo: { path: "logo.sourceUri.uri", required: true },
    wide_logo: { path: "wideLogo.sourceUri.uri", required: false },
    hero: { path: "heroImage.sourceUri.uri", required: false },
  },
  giftCard: { // giftCard
    logo: { path: "programLogo.sourceUri.uri", required: true },
    wide_logo: { path: "wideProgramLogo.sourceUri.uri", required: false },
    hero: { path: "heroImage.sourceUri.uri", required: false },
  },
  transit: {
    logo: { path: "logo.sourceUri.uri", required: true },
    wide_logo: { path: "wideLogo.sourceUri.uri", required: false },
    hero: { path: "heroImage.sourceUri.uri", required: false },
  },
  // TODO : Add google boardingPass images
}

const WizardImages = ({ setPrevButtonState, setNextButtonState, passTypeId, template, setTemplate, model, objectData, classData, setClassData, setGoogleBoth, setObjectData, applePassType, appleSubType, googlePassType, instanceData, setInstanceData }) => {

  const { imageList } = useContext(AppContext)

  const requiredImages = {
    source: googleSources[googlePassType],
    images: {
      apple: appleRequiredImages[applePassType][appleSubType] ?? appleRequiredImages[applePassType],
      google: googleRequiredImages[googlePassType]
    }
  }

  useEffect(() => {
    setPrevButtonState("enabled")
    setNextButtonState("disabled")
  }, [])

  useEffect(() => {

    let valid = true

    for (let i in requiredImages.images.apple) {
      const item = requiredImages.images.apple[i]
      const img = _.get(template, item.path)
      const assigned = imageList.filter(i => i.s3_url === img).length > 0
      if (item.required && !assigned) valid = false
    }

    for (let i in requiredImages.images.google) {
      const item = requiredImages.images.google[i]
      const img = _.get(requiredImages.source === "class" ? classData : objectData, item.path)
      const assigned = imageList.filter(i => i.s3_url === img).length > 0
      if (item.required && !assigned) valid = false
    }

    if (valid) {
      setNextButtonState("enabled")
    } else {
      setNextButtonState("disabled")
    }
  }, [template, objectData, classData])

  const googleSourceData = requiredImages.source === "class" ? classData : objectData

  return (
    <div className="mb-10">
      <Typography variant="h3" sx={{ p: 2 }}>Images</Typography>
      <PassContext.Provider value={{ device: "apple", template, passType: applePassType, setTemplate, model, instanceData, setInstanceData }}>
        <AppleImageSelector title="Apple Icon" template={template} path={requiredImages.images.apple.icon?.path} imageList={imageList} required={true} instanceData={instanceData} dataSelector={appleDataSelector}/>
        <AppleImageSelector title="Apple Logo" template={template} path={requiredImages.images.apple.logo?.path} imageList={imageList} required={true} instanceData={instanceData} dataSelector={appleDataSelector}/>
        {passTypeId !== "smartix_stamp" && <AppleImageSelector title="Apple Strip Image" template={template} path={requiredImages.images.apple.strip?.path} imageList={imageList} required={false} instanceData={instanceData} dataSelector={appleDataSelector}/>}
        <AppleImageSelector title="Apple Thumbnail Image" template={template} path={requiredImages.images.apple.thumbnail?.path} imageList={imageList} required={false} instanceData={instanceData} dataSelector={appleDataSelector}/>
        <AppleImageSelector title="Apple Background Image" template={template} path={requiredImages.images.apple.background?.path} imageList={imageList} required={false} instanceData={instanceData} dataSelector={appleDataSelector}/>
      </PassContext.Provider>

      <hr />

      <PassContext.Provider value={{ device: "google", passType: googlePassType, classData, objectData, setClassData, setObjectData, instanceData, setInstanceData, setGoogleBoth }}>
        <GoogleLogoSelector title="Google Logo" type="logo" sourceData={googleSourceData} paths={requiredImages.images.google} source={requiredImages.source} logoType="logo" imageList={imageList} required={true} />
        <GoogleLogoSelector title="Google Wide Logo" type="logo" sourceData={googleSourceData} paths={requiredImages.images.google} source={requiredImages.source} logoType="wideLogo" imageList={imageList} required={false} />
        {passTypeId !== "smartix_stamp" && <GoogleImageSelector title="Google Hero Image" type="image" sourceData={googleSourceData} path={requiredImages.images.google.hero?.path} source={requiredImages.source} imageList={imageList} required={false} instanceData={instanceData} dataSelector={googleDataSelector}/>}
      </PassContext.Provider>
    </div>
  )
}

export default WizardImages

const AppleImageSelector = ({ title, template, path, imageList, required, instanceData, dataSelector }) => {

  if (!path) return // Pass type not configured for this image

  const image = dataSelector({ template, instanceData, scannerInstanceData:{}, path})

  const isValid = imageList.filter(i => i.s3_url === image).length > 0
  const classes = !isValid && required ? "red" : ""

  return (
    <div className='flex flex-row justify-between'>
      <div className={classes}>{title}</div>
      <ApplePopupEditor title={title} path={path} wizard={true}>
        <div className="image" style={{ position: "relative", cursor: "pointer" }}>
          {image && <img src={image} style={{ height: 100, marginTop: 20 }} />}
          {!image && <div className="flex justify-around items-center" style={{ width: 250, height: 100, marginTop: 20, border: "1px dashed", borderRadius: 10 }}> No Image Selected</div>}
        </div>
      </ApplePopupEditor>
    </div>
  )
}

const GoogleLogoSelector = ({ title, sourceData, paths, imageList, type, logoType, source, required }) => {

  const images = {
    logo: _.get(sourceData, paths.logo.path),
    wideLogo: _.get(sourceData, paths.wide_logo.path)
  }

  const sources = {
    source: source,
    logo: {
      path: paths.logo.path,
      value: images.logo
    },
    wideLogo: {
      path: paths.wide_logo.path,
      value: images.wideLogo
    }
  }

  const isValid = imageList.filter(i => i.s3_url === images[logoType]).length > 0
  const classes = !isValid && required ? "red" : ""

  return (
    <div className='flex flex-row justify-between'>
      <div className={classes}>{title}</div>
      <GooglePopupEditor title={title} sources={sources} path={paths[logoType.path]} type={type} wizard={true} logoType={logoType}>
        <div className="image editable" style={{ position: "relative", cursor: "pointer" }}>
          {images[logoType] && <img src={images[logoType]} style={{ height: 100, marginTop: 20 }} />}
          {!images[logoType] && <div className="flex justify-around items-center" style={{ width: 250, height: 100, marginTop: 20, border: "1px dashed", borderRadius: 10 }}> No Image Selected</div>}
        </div>
      </GooglePopupEditor>
    </div>
  )
}


const GoogleImageSelector = ({ title, sourceData, path, imageList, type, source, required, instanceData, dataSelector }) => {

  if (!path) return // Pass type not configured for this image

  const image = dataSelector({ objectData:sourceData, instanceData, scannerInstanceData:{}, path})

  const sources = {
    template_source : source,
    path: path,
    value: image
  }

  const isValid = imageList.filter(i => i.s3_url === image).length > 0
  const classes = !isValid && required ? "red" : ""

  return (
    <div className='flex flex-row justify-between'>
      <div className={classes}>{title}</div>
      <GooglePopupEditor title={title} sources={sources} path={path} type={type} wizard={true}>
        <div className="image editable" style={{ position: "relative" }}>
          {image && <img src={image} style={{ height: 100, marginTop: 20 }} />}
          {!image && <div className="flex justify-around items-center" style={{ width: 250, height: 100, marginTop: 20, border: "1px dashed", borderRadius: 10 }}> No Image Selected</div>}
        </div>
      </GooglePopupEditor>
    </div>
  )
}